import qs from 'querystring'
import { BaseService, service } from '../base-service'

export class ResignationRequestProcessService extends BaseService {
  constructor() {
    super('/v2/resignation-request-process')
  }

  async getCollaboratoresWithResignationRequests(client, term, limit, page) {
    const query = qs.stringify({
      client,
      term,
      limit,
      page
    })

    const url = `${this.baseURL}/get-collaborators-with-resignation-requests?${query}`
    return service.get(url)
  }

  async generateResignation(resignationRequestId, data) {
    const {
      resignationDate,
      resignationReason,
      resignationJustification,
      resignationType,
      files
    } = data
    const url = `${this.baseURL}/${resignationRequestId}/generate-resignation`

    return service.post(url, {
      resignationDate,
      resignationReason,
      resignationJustification,
      resignationType,
      files
    })
  }

  async getCollaboratorsWithApprovedResignationRequest(
    client,
    keyword,
    limit,
    page
  ) {
    let skip = (page - 1) * limit
    if (skip < 0) {
      skip = 0
    }

    const query = qs.stringify({
      client,
      keyword,
      limit,
      skip
    })

    const url = `${this.baseURL}/get-resignation-request-approved?${query}`

    return service.get(url)
  }

  async getResignationRequestByKeyword(client, keyword, status, limit, page) {
    const query = qs.stringify({
      client,
      keyword,
      status,
      limit,
      page
    })
    const url = `${this.baseURL}/get-resignation-requests-by-keyword?${query}`
    return service.get(url)
  }

  async exportResignationRequests(client, keyword, status, lang) {
    const query = qs.stringify({
      client,
      keyword,
      status
    })
    const url = `${this.baseURL}/get-resignation-requests-by-keyword?${query}`

    return service.get(url, {
      headers: { 'x-custom-lang': lang },
      responseType: 'arraybuffer',
      params: {
        exportType: 'xlsx'
      }
    })
  }

  async getResignationRequestApproversByUnity(
    client,
    unity,
    candidateDocument
  ) {
    const query = qs.stringify({
      client,
      unity,
      candidateDocument
    })

    const url = `${this.baseURL}/get-resignation-request-approvers-by-unity?${query}`

    return service.get(url)
  }

  async getCompletedResignationsByFilter(
    client,
    unity,
    resignationForm,
    term,
    startDate,
    endDate,
    limit,
    skip
  ) {
    const query = qs.stringify({
      client,
      unity,
      resignationForm,
      term,
      startDate,
      endDate,
      limit,
      skip
    })

    const url = `${this.baseURL}/get-completed-resignations-by-filter?${query}`

    return service.get(url)
  }

  async exportCompletedResignationsByFilter(
    client,
    unity,
    resignationForm,
    term,
    startDate,
    endDate,
    lang
  ) {
    const query = qs.stringify({
      client,
      unity,
      resignationForm,
      term,
      startDate,
      endDate
    })

    const url = `${this.baseURL}/export-completed-resignations-by-filter?${query}`

    return service.get(url, {
      headers: { 'x-custom-lang': lang },
      responseType: 'arraybuffer'
    })
  }

  async getMyResignationRequest({ client, search }) {
    const url = `${this.baseURL}/${client}/my?search=${search}`
    return service.get(url)
  }

  async getRollbackPlans(client, term, limit, page) {
    const query = qs.stringify({
      client,
      term,
      limit,
      page
    })
    const url = `${this.baseURL}/rollback-plans?${query}`
    return service.get(url)
  }

  async getRollbackPlansByCandidate(client, candidate, limit) {
    const query = qs.stringify({
      client,
      candidate,
      limit
    })
    const url = `${this.baseURL}/rollback-plans-by-candidate?${query}`
    return service.get(url)
  }

  startResignationRequestProcessByResignationRequest(body) {
    return service.post(
      `${this.baseURL}/start-resignation-request-process-by-resignation-request/`,
      body
    )
  }

  startResignationRequestProcessByRollbackPlan(body) {
    return service.post(
      `${this.baseURL}/start-resignation-request-process-by-rollback-plan/`,
      body
    )
  }

  setRollbackPlanByResignationRequest(body) {
    return service.put(
      `${this.baseURL}/set-rollback-plan-by-resignation-request/`,
      body
    )
  }

  updateRollbackPlanInfos(id, body) {
    return service.put(
      `${this.baseURL}/${id}/update-rollback-plan-infos/`,
      body
    )
  }

  publishRollbackPlan(id, body) {
    return service.put(`${this.baseURL}/${id}/publish-rollback-plan/`, body)
  }

  deleteResignationRequest(id) {
    return service.delete(`${this.baseURL}/${id}/resignation-request`)
  }

  deleteRollbackPlan(id) {
    return service.delete(`${this.baseURL}/${id}/rollback-plan`)
  }

  approve(resignationProcessId) {
    return service.put(`${this.baseURL}/${resignationProcessId}/approve`)
  }

  disapprove(id, disapprovalJustification) {
    return service.put(`${this.baseURL}/${id}/disapprove/`, {
      disapprovalJustification
    })
  }

  executeFollow(id, subId, body) {
    return service.put(`${this.baseURL}/${id}/execute-follow/${subId}`, body)
  }

  addFollowActionAttachments(id, subId, actionIndex, files) {
    actionIndex = actionIndex + 1
    const url = `${this.baseURL}/${id}/follow/${subId}/action/${actionIndex}/add-attachments`

    const formData = new FormData()

    if (files && files.length > 0) {
      files.forEach((file) => formData.append('files', file))
    } else {
      throw new Error('files is required')
    }

    return service.put(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  }

  removeFollowActionAttachments(id, subId, actionIndex, fileReferences) {
    actionIndex = actionIndex + 1
    const url = `${this.baseURL}/${id}/follow/${subId}/action/${actionIndex}/remove-attachments`

    return service.put(url, { fileReferences })
  }

  exportResignationRequestsToExcel(client, lang) {
    if (!client) {
      throw new Error('Client name is required!')
    }

    return service.get(
      `${this.baseURL}/${client}/export-resignation-requests-to-excel`,
      {
        headers: { 'x-custom-lang': lang },
        responseType: 'arraybuffer'
      }
    )
  }

  validateIfIsDuplicatedRollbackPlanName(client, name, candidateDocument) {
    const query = qs.stringify({
      client,
      name,
      candidateDocument
    })
    return service.get(
      `${this.baseURL}/validate-if-rp-name-has-been-used?${query}`
    )
  }
}

export const resignationRequestProcessService =
  new ResignationRequestProcessService()
